/**
 * Sign In Page for BNOVA Business Dashboard
 * Integrated with authentication backend.
 * Role: Default = Upload Library + Logout only; Super = full access (requires SUPER_ACCESS_KEY).
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Authentication context
import { useAuth } from "context/AuthContext";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

const ROLE_DEFAULT = "default";
const ROLE_SUPER = "super";

function SignIn() {
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roleType, setRoleType] = useState(ROLE_DEFAULT);
  const [superAccessKey, setSuperAccessKey] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Redirect if already authenticated (destination depends on role)
  if (isAuthenticated) {
    const dest = user?.roleType === ROLE_SUPER ? "/user-analytics" : "/upload-library";
    navigate(dest);
    return null;
  }

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!email || !password) {
      setError("Please fill in email and password");
      setLoading(false);
      return;
    }
    if (roleType === ROLE_SUPER && !superAccessKey) {
      setError("Super access requires the super access password");
      setLoading(false);
      return;
    }

    const result = await login(email, password, roleType, superAccessKey);

    if (result.success) {
      const dest = roleType === ROLE_SUPER ? "/user-analytics" : "/upload-library";
      navigate(dest);
    } else {
      setError(result.error || "Login failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign In
          </MDTypography>
          <MDTypography variant="body2" color="white" mt={1}>
            BNova Business Dashboard
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            {error && (
              <MDBox mb={2}>
                <Alert severity="error">{error}</Alert>
              </MDBox>
            )}

            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </MDBox>
            <MDBox mb={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="signin-role-label">Access role</InputLabel>
                <Select
                  labelId="signin-role-label"
                  label="Access role"
                  value={roleType}
                  onChange={(e) => {
                    setRoleType(e.target.value);
                    setSuperAccessKey("");
                    setError("");
                  }}
                  disabled={loading}
                >
                  <MenuItem value={ROLE_DEFAULT}>
                    Default role (Upload Library &amp; Logout only)
                  </MenuItem>
                  <MenuItem value={ROLE_SUPER}>Super access (full dashboard)</MenuItem>
                </Select>
              </FormControl>
            </MDBox>
            {roleType === ROLE_SUPER && (
              <MDBox mb={2}>
                <MDInput
                  type="password"
                  label="Super access password"
                  fullWidth
                  value={superAccessKey}
                  onChange={(e) => setSuperAccessKey(e.target.value)}
                  disabled={loading}
                  placeholder="Enter SUPER_ACCESS_KEY"
                />
              </MDBox>
            )}
            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} disabled={loading} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Remember me
              </MDTypography>
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton variant="gradient" color="info" fullWidth type="submit" disabled={loading}>
                {loading ? "Signing in..." : "Sign In"}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Don&apos;t have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-up"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign Up
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default SignIn;
